import { useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useMeasuredWidth } from '@/shared/ui/useMeasuredWidth';

import type { BaselineBand, SeriesPoint } from '../api/anomaly';

/* The observed series against its expected band, as the redesign draws it.
 *
 * **Feature-local, not `Sparkline`.** `Sparkline` draws a line and nothing else; this one states
 * three things in the picture that the panel also states in words — the observed line, the expected
 * band, and which point is the one under test — so a reader who cannot see the shape has lost
 * nothing.
 *
 * Every reading is a dot, and the latest is the large one: it turns the alert hue only when it falls
 * outside the band, because a red marker on a value inside its range would claim a problem the
 * statistics did not find. **No band is drawn and said so** when the history is too short or too
 * flat to have one — an invented band would be a range nobody measured.
 *
 * Drawn at the measured pixel width rather than stretched, so the dots stay round and the date labels
 * stay legible however wide the panel is. Draws nothing below two points — one reading is not a trend.
 */

const HEIGHT = 210;
const PAD = { left: 4, right: 10, top: 16, bottom: 24 } as const;
const FALLBACK_WIDTH = 600;

export interface SeriesChartProps {
  readonly points: readonly SeriesPoint[];
  readonly band: BaselineBand | null;
  /** Accessible name — the caption states the movement in words; this carries the shape. */
  readonly label: string;
  /** How a value reads in the tooltip — hours for freshness, a compact count for volume. */
  readonly format?: (value: number) => string;
}

export function SeriesChart({
  points,
  band,
  label,
  format = (value) => value.toLocaleString(),
}: SeriesChartProps): ReactElement | null {
  const { t } = useTranslation();
  const [ref, width] = useMeasuredWidth<HTMLDivElement>(FALLBACK_WIDTH);
  const [hover, setHover] = useState<number | null>(null);

  if (points.length < 2) return null;

  const values = points.map((point) => point.value);
  const all = band === null ? values : [...values, band.low, band.high];
  let low = Math.min(...all);
  let high = Math.max(...all);
  const spread = high - low || Math.abs(high) * 0.1 || 1;
  low -= spread * 0.15;
  high += spread * 0.15;

  const innerWidth = width - PAD.left - PAD.right;
  const innerHeight = HEIGHT - PAD.top - PAD.bottom;
  const last = points.length - 1;
  const x = (index: number): number =>
    PAD.left + (index / Math.max(last, 1)) * innerWidth;
  const y = (value: number): number =>
    PAD.top + innerHeight - ((value - low) / (high - low)) * innerHeight;

  const line = values
    .map(
      (value, index) =>
        `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)} ${y(value).toFixed(1)}`,
    )
    .join(' ');
  const latest = values[last] ?? 0;
  const outside = band !== null && (latest < band.low || latest > band.high);
  const day = (iso: string): string =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

  const onMove = (event: MouseEvent<HTMLDivElement>): void => {
    const box = event.currentTarget.getBoundingClientRect();
    const index = Math.round(((event.clientX - box.left - PAD.left) / innerWidth) * last);
    setHover(Math.max(0, Math.min(last, index)));
  };

  const hovered = hover === null ? null : points[hover];

  return (
    <div
      ref={ref}
      className="lq-serieschart"
      onMouseMove={onMove}
      onMouseLeave={() => {
        setHover(null);
      }}
    >
      <svg width={width} height={HEIGHT} role="img" aria-label={label}>
        {band === null ? (
          <text className="lq-serieschart__note" x={PAD.left + 6} y={PAD.top}>
            {t('anomaly.chart.noBand')}
          </text>
        ) : (
          <>
            <rect
              className="lq-serieschart__band"
              x={PAD.left}
              width={innerWidth}
              y={y(band.high)}
              height={Math.max(y(band.low) - y(band.high), 1)}
              rx={4}
            />
            <text
              className="lq-serieschart__bandLabel"
              x={PAD.left + 6}
              y={y(band.high) - 5}
            >
              {t('anomaly.chart.expected')}
            </text>
          </>
        )}
        <path className="lq-serieschart__line" d={line} />
        {values.map((value, index) => (
          <circle
            key={points[index]?.observed_at ?? index}
            className={
              index === last
                ? outside
                  ? 'lq-serieschart__dot lq-serieschart__dot--alert'
                  : 'lq-serieschart__dot lq-serieschart__dot--latest'
                : 'lq-serieschart__dot'
            }
            cx={x(index)}
            cy={y(value)}
            r={index === last ? 5.5 : hover === index ? 4 : 2.2}
          />
        ))}
        {[0, last].map((index) => (
          <text
            key={index}
            className="lq-serieschart__date"
            x={x(index)}
            y={HEIGHT - 6}
            textAnchor={index === 0 ? 'start' : 'end'}
          >
            {day(points[index]?.observed_at ?? '')}
          </text>
        ))}
      </svg>
      {hovered === null || hovered === undefined || hover === null ? null : (
        <div
          className="lq-serieschart__tip"
          style={{
            left: Math.min(Math.max(x(hover) - 60, 0), width - 120),
            top: Math.max(y(hovered.value) - 56, -10),
          }}
        >
          <div className="lq-serieschart__tipDay">{day(hovered.observed_at)}</div>
          <div className="lq-serieschart__tipValue">{format(hovered.value)}</div>
        </div>
      )}
    </div>
  );
}
